import type { ReactElement } from 'react';
import React, { cloneElement, useEffect } from 'react';
import type { ButtonProps } from 'components/buttons/Button/types';

import { BiggerSubtitle } from '../../../foundations/typefaces/BiggerSubtitle';
import BaseModal from '../BaseModal/BaseModal';
import type { BaseModalProps } from '../types';
import {
  ButtonWrapper,
  ContentWrapper,
  FooterWrapper,
  ImageWrapper,
  StyledBasicParagraph,
  StyledHeader,
  StyledImage,
} from './WarningModal.styled';

type OmittedProps = 'height' | 'minHeight' | 'width' | 'children';

type ImageWarningModalProps =
  | {
      /**
       * Indicates that the modal will not have an image and should not reserve the space that is reserved prior to loading
       */
      withoutImage?: false;
      /**
       * url of the image that will be displayed on the modal
       */
      imageUrl: string;
    }
  | {
      /**
       * Indicates that the modal will not have an image and should not reserve the space that is reserved prior to loading
       */
      withoutImage: true;
      /**
       * url of the image that will be displayed on the modal
       */
      imageUrl?: undefined;
    };

interface WarningModalProps extends Omit<BaseModalProps, OmittedProps> {
  /**
   * title to display on the modal
   */
  title: string;
  /**
   * description to display on the modal
   */
  description?: string;
  /**
   * button which will carry out the primary action
   */
  PrimaryButton: ReactElement;
  /**
   * button which will carry out the secondary action
   */
  SecondaryButton?: ReactElement;
  /**
   * checkbox that can be displayed in the modal content, normally for the user to be able to mark if they don't want to receive this warning again
   */
  Checkbox?: ReactElement;
}

export const WarningModal = ({
  dataCy,
  parentElement,
  close,
  title,
  description,
  PrimaryButton,
  SecondaryButton,
  Checkbox,
  imageUrl,
  withoutImage = false,
}: WarningModalProps & ImageWarningModalProps) => {
  const ClonedPrimaryButton = (
    props: (Partial<ButtonProps> & React.Attributes) | undefined,
  ) => cloneElement(PrimaryButton as ReactElement, props);

  const ClonedSecondaryButton = (
    props: (Partial<ButtonProps> & React.Attributes) | undefined,
  ) => cloneElement(SecondaryButton as ReactElement, props);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        PrimaryButton.props.onClick?.(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [PrimaryButton]);

  return (
    <BaseModal
      dataCy={dataCy}
      width={BaseModal.Size.SMALL}
      parentElement={parentElement}
      close={close}
    >
      <BaseModal.Header>
        <StyledHeader>
          {!withoutImage && (
            <ImageWrapper>
              <StyledImage alt="" src={imageUrl} data-cy="warningModalImage" />
            </ImageWrapper>
          )}
          <BiggerSubtitle>{title}</BiggerSubtitle>
        </StyledHeader>
      </BaseModal.Header>
      <BaseModal.Content>
        <ContentWrapper>
          {description && (
            <StyledBasicParagraph dangerouslySetInnerHTML={{ __html: description }} />
          )}
          {Checkbox && Checkbox}
        </ContentWrapper>
      </BaseModal.Content>
      <BaseModal.Footer>
        <FooterWrapper>
          {SecondaryButton && (
            <ButtonWrapper>
              <ClonedSecondaryButton width="100%" />
            </ButtonWrapper>
          )}
          <ButtonWrapper $isOnlyButton={!SecondaryButton}>
            <ClonedPrimaryButton width="100%" />
          </ButtonWrapper>
        </FooterWrapper>
      </BaseModal.Footer>
    </BaseModal>
  );
};
